import React, { useState } from "react";
import styled from "styled-components";
import SearchIcon from "@material-ui/icons/Search";
import { camelCase, includes, xorBy } from "lodash";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
  FilterHeader,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Input } from "@sonder/ui/source/styled/fragments/Input";
import { InputWithIcons } from "@sonder/ui/source/styled/fragments/InputWithIcons";
import { colors } from "@sonder/ui/source/styled/theme";
import {
  UnitConsumables,
  useGetUnitConsumablesQuery,
} from "core/queries/GetUnitConsumables";
import { SearchForTaskSubjectsItem } from "core/queries/SearchForTaskSubjects";
import { ConsumablesFilterGroupItem } from "tasks/components/ConsumablesFilterGroupItem";
import {
  Result,
  TaskContextSubjectTypeEnum,
  TypeEnum,
} from "typings/globalGQLTypes";

export interface Subject {
  displayName: string;
  resultType: Result;
  subjectType: TaskContextSubjectTypeEnum;
  searchPlaceholder: string;
  validTaskTypes: TypeEnum[];
}

const StyledFilterButtonList = styled(FilterButtonList)`
  max-width: inherit;
`;

const StyledFilterFooter = styled(FilterFooter)`
  display: flex;
  justify-content: flex-end;
`;

const StyledAddButton = styled(Button)`
  min-width: 6.5rem;
`;

const StyledError = styled.p`
  color: ${colors.redD};
`;

interface TaskConsumablesSearchProps {
  subjectId: string | null;
  subject: SearchForTaskSubjectsItem | null;
  onAddConsumable: (consumable: UnitConsumables[]) => void;
}

type UnitConsumablesSearch = Omit<TaskConsumablesSearchProps, "subject">;

export const UnitConsumablesSearch = ({
  subjectId,
  onAddConsumable,
}: UnitConsumablesSearch) => {
  const [search, setSearch] = useState("");
  const [consumables, setConsumables] = useState<UnitConsumables[]>([]);

  const { data, loading, error } = useGetUnitConsumablesQuery({
    variables: {
      id: subjectId || "",
    },
    skip: !subjectId,
  });

  const searchOptions =
    data?.unit?.unitConsumables?.map(field => ({
      ...field,
      metadataKey: camelCase(field?.metadataKey),
    })) || [];

  const filteredOptions =
    search !== ""
      ? searchOptions?.filter(option =>
          option?.name.toLowerCase().includes(search.toLowerCase()),
        )
      : searchOptions;

  const isAllSelected =
    Boolean(consumables.length) &&
    consumables.length === filteredOptions.length;

  const onSelectConsumable = (option: UnitConsumables) => {
    const newConsumables = xorBy(consumables, [option], "metadataKey");
    setConsumables(newConsumables);
  };

  const onSelectAllConsumables = () => {
    const newConsumables =
      consumables.length === filteredOptions.length ? [] : filteredOptions;
    setConsumables(newConsumables);
  };

  const renderConsumable = (option: UnitConsumables) => {
    const isChecked = includes(
      consumables.map(x => x?.name),
      option?.name,
    );
    return (
      <ConsumablesFilterGroupItem
        isChecked={isChecked}
        key={option?.name}
        label={option?.name}
        onClick={() => onSelectConsumable(option)}
      />
    );
  };
  const isDisabled = !consumables.length;

  const isSearchTermEmpty = search === "";
  const isEmpty = filteredOptions?.length === 0;
  const isEmptyAndLoading = isEmpty && loading;
  const isEmptyAndSearched = !loading && isEmpty && !isSearchTermEmpty;

  return (
    <Dropdown
      trigger={
        <Button fullWidth iconRight type="button">
          {search === "" ? "Search Consumables & Linens" : search}
          <ChevronDown />
        </Button>
      }
    >
      {({ isOpen: isPortalOpen, closePortal }) =>
        isPortalOpen && (
          <FilterDropdownContent align="left">
            <InputWithIcons childLeft childRight>
              <SearchIcon />
              <Input
                autoFocus
                placeholder="Search Consumables & Linens"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </InputWithIcons>
            {error && error.message && (
              <StyledError>There was an error: {error.message}</StyledError>
            )}
            {isEmptyAndLoading && <FilterEmpty>Searching...</FilterEmpty>}
            {isEmptyAndSearched && (
              <FilterEmpty>{`No Results for ${search}.`}</FilterEmpty>
            )}
            {!isEmpty && !isEmptyAndSearched && (
              <FilterHeader>
                <ConsumablesFilterGroupItem
                  isChecked={isAllSelected}
                  label="Select All"
                  onClick={() => onSelectAllConsumables()}
                />
              </FilterHeader>
            )}
            <StyledFilterButtonList>
              {filteredOptions?.map(renderConsumable)}
            </StyledFilterButtonList>
            <StyledFilterFooter>
              <StyledAddButton
                disabled={isDisabled}
                onClick={() => {
                  onAddConsumable(consumables);
                  setConsumables([]);
                  closePortal();
                }}
              >
                Add
              </StyledAddButton>
            </StyledFilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};

export const TaskConsumablesSearch = ({
  subjectId,
  subject,
  onAddConsumable,
}: TaskConsumablesSearchProps) => {
  switch (subject?.resource?.__typename) {
    case TaskContextSubjectTypeEnum.LeaseInquiry:
    case TaskContextSubjectTypeEnum.Inquiry:
      return (
        <UnitConsumablesSearch
          subjectId={subject?.resource?.unit.id}
          onAddConsumable={onAddConsumable}
        />
      );

    case TaskContextSubjectTypeEnum.Unit:
      return (
        <UnitConsumablesSearch
          subjectId={subjectId}
          onAddConsumable={onAddConsumable}
        />
      );

    default:
      return null;
  }
};
